
import { useState, useEffect, useCallback } from 'react';
import {
    MetaAdAccount,
    MetaPixel,
    MetaPage,
    fetchMetaPixels
} from '../../../services/metaService';

interface UseMetaIntegrationProps {
    accessToken: string;
    accounts: MetaAdAccount[];
    pages: MetaPage[];
}

export const useMetaIntegration = ({
    accessToken,
    accounts,
    pages
}: UseMetaIntegrationProps) => {
    // Selection State
    const [selectedAccount, setSelectedAccount] = useState<MetaAdAccount | null>(null);
    const [selectedPixel, setSelectedPixel] = useState<MetaPixel | null>(null);
    const [selectedPage, setSelectedPage] = useState<MetaPage | null>(null);

    // Dynamic Data State
    const [pixels, setPixels] = useState<MetaPixel[]>([]);
    const [loadingPixels, setLoadingPixels] = useState(false);

    // Effect: Load pixels when account changes
    useEffect(() => {
        if (selectedAccount && accessToken) {
            setLoadingPixels(true);
            setSelectedPixel(null);

            fetchMetaPixels(selectedAccount.id, accessToken)
                .then(data => {
                    setPixels(data);
                    // Auto-select if only one pixel exists
                    if (data.length === 1) {
                        setSelectedPixel(data[0]);
                    }
                })
                .catch(err => {
                    console.error('Failed to fetch pixels:', err instanceof Error ? err.message : err);
                    setPixels([]);
                })
                .finally(() => setLoadingPixels(false));
        } else {
            setPixels([]);
        }
    }, [selectedAccount, accessToken]);

    // Effect: Auto-select account if only one exists
    useEffect(() => {
        if (accounts.length === 1 && !selectedAccount) {
            setSelectedAccount(accounts[0]);
        }
    }, [accounts, selectedAccount]);

    // Effect: Auto-select page if only one exists
    useEffect(() => {
        if (pages.length === 1 && !selectedPage) {
            setSelectedPage(pages[0]);
        }
    }, [pages, selectedPage]);

    // Handlers
    const handleAccountChange = useCallback((accountId: string) => {
        const account = accounts.find(a => a.id === accountId);
        setSelectedAccount(account || null);
    }, [accounts]);

    const handlePixelChange = useCallback((pixelId: string) => {
        const pixel = pixels.find(p => p.id === pixelId);
        setSelectedPixel(pixel || null);
    }, [pixels]);

    const handlePageChange = useCallback((pageId: string) => {
        const page = pages.find(p => p.id === pageId);
        setSelectedPage(page || null);
    }, [pages]);

    return {
        // State
        selectedAccount,
        selectedPixel,
        selectedPage,
        pixels,
        loadingPixels,
        // Página é obrigatória para eventos CTWA funcionarem corretamente
        canSave: selectedAccount !== null && selectedPage !== null,

        // Setters/Handlers
        handleAccountChange,
        handlePixelChange,
        handlePageChange
    };
};
